/**
 * Les entretiens réalisés d'une machine, à l'écran (lot B2) : lecture des saisies, phrases, règles
 * d'affichage. Fonctions pures, sans React : c'est ici que se décide ce qu'on accepte et ce qu'on dit.
 *
 * AUCUNE règle de calcul ici (le serveur décide de l'état, du retard et du prochain point). Ce module
 * lit ce que l'utilisateur tape — à la française — et le met en forme avant l'envoi.
 */
import type { DernierEntretien, EntreeEntretienRealise, EntretienRealise, ProchainEntretien } from '../api/client';
import { lireNombre } from './gammes-edition';
import { dateFr, nombre } from './gammes';

/** Un palier proposé dans le menu : de quoi l'afficher et le retrouver (jamais présélectionné). */
export interface NiveauChoix {
  readonly levelKey: string;
  readonly code: string;
  readonly label: string;
  readonly rank: number;
}

/** Les paliers du moins complet au plus complet (le rang), sans les lignes de pièces ni les prix. */
export function niveauxPourChoix(
  niveaux: readonly { readonly levelKey: string; readonly code: string; readonly label: string; readonly rank: number }[],
): NiveauChoix[] {
  return niveaux
    .map(({ levelKey, code, label, rank }) => ({ levelKey, code, label, rank }))
    .sort((a, b) => a.rank - b.rank);
}

/**
 * « Palier A » ou « Palier B — Entretien de base » : le libellé n'est répété que s'il dit autre chose que le
 * code (les gammes nomment souvent leur palier « Palier A », qui ne se redit pas).
 */
export function libelleNiveau(niveau: Pick<NiveauChoix, 'code' | 'label'>): string {
  const code = niveau.code.trim();
  const label = niveau.label.trim();
  const redondant = [code.toLowerCase(), `palier ${code.toLowerCase()}`];
  if (label === '' || redondant.includes(label.toLowerCase())) return `Palier ${code}`;
  return `Palier ${code} — ${label}`;
}

/** Un jour AAAA-MM-JJ qui existe vraiment (le 30 février est refusé, jamais « corrigé » en silence). */
function jourExiste(jour: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return false;
  const verif = new Date(`${jour}T00:00:00.000Z`);
  return !Number.isNaN(verif.getTime()) && verif.toISOString().slice(0, 10) === jour;
}

export type LectureJour = { readonly jour: string } | { readonly erreur: string };

/** La date d'un entretien : obligatoire, qui existe, pas après aujourd'hui (le jour que voit l'utilisateur). */
export function lireJourEntretien(saisie: string, aujourdhui: string): LectureJour {
  const jour = saisie.trim();
  if (jour === '') return { erreur: 'Indiquez la date de l’entretien.' };
  if (!jourExiste(jour)) return { erreur: 'Cette date n’existe pas : choisissez-la dans le calendrier.' };
  if (jour > aujourdhui) return { erreur: 'Un entretien ne peut pas être daté dans le futur.' };
  return { jour };
}

export type LectureHeures = { readonly valeur: string | null } | { readonly erreur: string };

/**
 * Les heures lues au compteur, à la française : « 22 577 » → « 22577 », « 1250,5 » → « 1250.5 ». Vide = non
 * renseignées. Rendues en CHAÎNE, jamais converties en nombre : un index peut dépasser la précision d'un
 * flottant, et le serveur les lit comme des décimaux.
 */
export function lireHeuresEntretien(saisie: string): LectureHeures {
  const lecture = lireNombre(saisie);
  if (!lecture.ok) return { erreur: lecture.erreur };
  if (lecture.valeur === null) return { valeur: null };
  if (lecture.valeur < 0) return { erreur: 'Les heures d’un entretien ne peuvent pas être négatives.' };
  return { valeur: saisie.replace(/\s/g, '').replace(',', '.') };
}

export interface SaisieEntretien {
  readonly levelKey: string;
  readonly date: string;
  readonly heures: string;
  readonly note: string;
  /** La case « relever aussi le compteur » telle que cochée. */
  readonly releverAussi: boolean;
}

export interface ErreursEntretien {
  readonly palier?: string;
  readonly date?: string;
  readonly heures?: string;
}

export interface ContexteSaisie {
  /** Le jour que voit l'utilisateur (heure locale), AAAA-MM-JJ. */
  readonly aujourdhui: string;
  /** Un compteur désigné pour les entretiens existe : seules ses heures ont une échelle. */
  readonly compteurDesigne: boolean;
  /** Droit d'écrire un relevé de compteur (`meter:record:tenant`). */
  readonly peutReleverCompteurs: boolean;
}

export type ValidationEntretien =
  | { readonly ok: true; readonly entree: EntreeEntretienRealise }
  | { readonly ok: false; readonly erreurs: ErreursEntretien };

/**
 * Contrôle la saisie AVANT l'envoi, avec des messages français : le serveur refuse les mêmes cas, mais l'écran
 * dit pourquoi à côté du champ fautif. Le palier n'est JAMAIS présélectionné : un palier se choisit.
 */
export function validerEntretien(saisie: SaisieEntretien, contexte: ContexteSaisie, corrigeId?: string): ValidationEntretien {
  const erreurs: { palier?: string; date?: string; heures?: string } = {};

  if (saisie.levelKey === '') erreurs.palier = 'Choisissez le palier réalisé.';

  const jour = lireJourEntretien(saisie.date, contexte.aujourdhui);
  if ('erreur' in jour) erreurs.date = jour.erreur;

  const heures = lireHeuresEntretien(saisie.heures);
  if ('erreur' in heures) {
    erreurs.heures = heures.erreur;
  } else if (heures.valeur !== null && !contexte.compteurDesigne) {
    erreurs.heures = 'Désignez d’abord le compteur des entretiens (heures totales) : des heures n’ont de sens que sur un compteur.';
  }

  const releve = peutReleverAussi(contexte, heures);
  if (saisie.releverAussi && !releve) {
    erreurs.heures ??= 'Pour relever aussi le compteur, indiquez les heures de l’entretien.';
  }

  if ('erreur' in jour || 'erreur' in heures || erreurs.palier || erreurs.date || erreurs.heures) {
    return { ok: false, erreurs };
  }

  return {
    ok: true,
    entree: {
      levelKey: saisie.levelKey,
      performedOn: jour.jour,
      ...(heures.valeur === null ? {} : { counterValue: heures.valeur }),
      ...(saisie.note.trim() === '' ? {} : { note: saisie.note.trim() }),
      ...(saisie.releverAussi && releve ? { releverAussi: true } : {}),
      ...(corrigeId ? { corrigeId } : {}),
    },
  };
}

/**
 * « Relever aussi le compteur » ne se propose qu'avec les trois conditions : le droit de relever, un compteur
 * désigné, et des heures saisies. Sans le droit, la case n'apparaît pas du tout (le serveur refuserait).
 */
export function peutReleverAussi(contexte: ContexteSaisie, heures: LectureHeures): boolean {
  return (
    contexte.peutReleverCompteurs && contexte.compteurDesigne && 'valeur' in heures && heures.valeur !== null
  );
}

/** « Dernier entretien : palier C le 21/08/2025, à 22 577 h » — sans les heures quand elles ne sont pas connues. */
export function phraseDernierEntretien(dernier: DernierEntretien | null): string | null {
  if (!dernier) return null;
  const heures = dernier.counterValue === null ? '' : `, à ${heuresTexte(dernier.counterValue)}`;
  return `Dernier entretien : palier ${dernier.levelCode} le ${dateFr(dernier.performedOn)}${heures}`;
}

/**
 * Les heures telles qu'on les remet dans la case d'une rectification : « 22577.000000 » → « 22577 »,
 * « 1250.500000 » → « 1250,5 » (virgule française, sans zéros inutiles ni séparateur de milliers).
 */
export function heuresPourSaisie(valeur: string | null): string {
  if (valeur === null) return '';
  const n = Number(valeur);
  return Number.isFinite(n) ? String(n).replace('.', ',') : '';
}

/** « 22 577 h » depuis la chaîne du serveur (« 22577.000000 »). */
export function heuresTexte(valeur: string): string {
  const n = Number(valeur);
  return Number.isFinite(n) ? `${nombre(n)} h` : `${valeur} h`;
}

export type EtatLigne = 'EFFECTIF' | 'REMPLACE' | 'ANNULE';

/** L'état d'une ligne de l'historique : un entretien annulé ou remplacé reste lisible, mais ne compte plus. */
export function etatLigne(entretien: Pick<EntretienRealise, 'annulee' | 'remplacee'>): EtatLigne {
  if (entretien.annulee) return 'ANNULE';
  if (entretien.remplacee) return 'REMPLACE';
  return 'EFFECTIF';
}

export const LIBELLE_ETAT_LIGNE: Record<EtatLigne, string> = {
  EFFECTIF: '',
  REMPLACE: 'Remplacé par une rectification',
  ANNULE: 'Annulé',
};

/** « Palier C · 21/08/2025 · 22 577 h » : la ligne de l'historique. */
export function phraseEntretien(e: Pick<EntretienRealise, 'levelCode' | 'performedOn' | 'counterValue'>): string {
  const morceaux = [`Palier ${e.levelCode}`, dateFr(e.performedOn)];
  if (e.counterValue !== null) morceaux.push(heuresTexte(e.counterValue));
  return morceaux.join(' · ');
}

/**
 * Pourquoi la carte est « bientôt » quand c'est par les HEURES : sans cette phrase, un « bientôt, vers le … » à
 * six mois laisserait croire à une date proche. La date, elle, se dit toute seule.
 */
export function messageBientotParLesHeures(vue: Pick<ProchainEntretien, 'etat' | 'bientotPar' | 'prochain'>): string | null {
  if (vue.etat !== 'BIENTOT' || vue.bientotPar !== 'HEURES' || !vue.prochain) return null;
  return `Il ne reste que ${nombre(vue.prochain.reste)} h avant ce point (moins de 5 % de l’écart entre deux points) : bientôt, même si la date estimée paraît lointaine.`;
}

/** Le texte de confirmation avant d'annuler : ce que cela fait, et ce que cela ne fait pas. */
export function confirmationAnnulation(e: Pick<EntretienRealise, 'levelCode' | 'performedOn' | 'readingId'>): string {
  const lignes = [
    `Annuler l’entretien du palier ${e.levelCode} du ${dateFr(e.performedOn)} ?`,
    '',
    'Il restera lisible dans l’historique, marqué « annulé », et ne comptera plus pour le prochain entretien.',
  ];
  if (e.readingId) {
    lignes.push(
      '',
      'Le relevé de compteur écrit avec cet entretien, lui, RESTE dans les compteurs (un relevé ne s’efface pas) : rectifiez-le dans l’historique du compteur s’il est faux.',
    );
  }
  return lignes.join('\n');
}
